import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { ObjectTypes } from '../enums/objectTypes';
import { ResponseModel } from '../models/responseModel';
import { FilterTypes } from '../enums/filterTypes';
import { Account } from '../models/account';
import { AuthenticationService } from './authentication.service';
import { Employee } from '../models/employee';
import { OperationTypes } from '../enums/operationTypes';
import { EmployeeUtil } from '../utilities/employee.utilities';
import { ModuleTypes } from '../../settings/enums/moduleTypes';
import { ObjectEventEmitters } from '../events/object.events';
import { EmployeeDetailsViewModel } from '../viewModels/employeeDetailsViewModel';
import { IncidentItemDetailsViewModel } from '../../incidents/viewModels/incidentItemDetailsViewModel';
import { PrivacyStatuses } from '../enums/privacyStatuses';
import { IncidentItemTypes } from '../../incidents/enums/incidentItemTypes';
import { NotificationsService } from './notifications.service';
import { AllowedFiltersService } from './allowed-filters.service';
import { LocalisationService } from './localisation.service';
import { FilterViewModel } from '../models/filter/filterViewModel';
import { EnumBasedLocalisationViewModel } from '../viewModels/enumBasedLocalisationViewModel';
import { Configuration } from 'src/app/app.constants';
import { SignalREventEmitters } from '../events/signalr.events';
import { AlertService } from './alert.service';
import { SignalRToastrConfig } from '../utilities/signalr-toastr-config.utilities';
import { IHttpConnectionOptions } from '@microsoft/signalr';
import { AuthService } from '../../auth/auth.service';
import { HistoryEntryViewModel } from '../viewModels/history-feed/historyEntryViewModel';
import { WtStorageService } from './wt-storage.service';
import { NotificationItemViewModel } from '../viewModels/notificationItemViewModel';
import { TimeZoneService } from './timeZone.service';
import { TimeShiftLockItem } from '../models/timeShiftLockModel';
import { AsyncOperationCompletedBroadcastModel, ExportCompletedBroadcastModel } from '../models/signalR/signalR-broadcast-models';
import { EmployeeSettingsService } from './employee-settings.service';



@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection: signalR.HubConnection;
  private account: Account;
  private employee: Employee;
  private tastrConfig = new SignalRToastrConfig();

  // Store connection params for reconnection (especially for external users)
  private lastEmployeeId?: number;
  private lastAccountId?: number;
  private lastAccessToken?: string;

  constructor(
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly localisationService: LocalisationService,
    private readonly authenticationService: AuthenticationService,
    private readonly authService: AuthService,
    private readonly objectEventEmitter: ObjectEventEmitters,
    private readonly notificationsService: NotificationsService,
    private readonly configuration: Configuration,
    private readonly signalREventEmitters: SignalREventEmitters,
    private readonly alertService: AlertService,
    private readonly wtStorage: WtStorageService,
    private readonly timezoneService: TimeZoneService,
    private readonly employeeSettingsService: EmployeeSettingsService
  ) {}

  public startConnection = (employeeId?: number, accountId?: number, accessToken?: string) => {
    // Store params for reconnection (especially important for external users)
    this.lastEmployeeId = employeeId;
    this.lastAccountId = accountId;
    this.lastAccessToken = accessToken;

    // For external users, use provided IDs; otherwise use authenticated user info
    if (employeeId && accountId) {
      this.employee = { id: employeeId } as Employee;
      this.account = { id: accountId } as Account;
    } else {
      this.account = this.authenticationService.getCurrentAccount();
      this.employee = this.authenticationService.getCurrentEmployee();
    }

    const options: IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: 1, // WebSockets
    };

    this.startDataConnection(options, accessToken);
  };

  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop();
      this.lastEmployeeId = undefined;
      this.lastAccountId = undefined;
      this.lastAccessToken = undefined;
    }
  }

  public addListener = () => {
    this.hubConnection.on('data', (response: [ResponseModel] | ResponseModel) => {
      let data = response[0];
      if (!data) {
        data = response;
      }
      // structuredClone strips Firefox XrayWrapper from cross-origin WebSocket objects,
      // preventing "Not allowed to define cross-origin object as property" errors.
      data = structuredClone(data);

      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      if (this.account && data.accountId && data.accountId === this.account.id) {
        // Own-record side effect (logout on delete/deactivate, live permission refresh
        // otherwise). This runs ADDITIONALLY to - not instead of - the dispatch below, so
        // any list/table already reacting to Employee update broadcasts (for every
        // employee, including the current one) keeps doing so unchanged.
        if (
          data.type === ObjectTypes.Employee &&
          (data.operationType === OperationTypes.Delete || data.operationType === OperationTypes.Update) &&
          // Different senders shape this payload differently: EmployeeDetailsViewModel-based
          // broadcasts carry `id`, other channels carry `employeeID` - accept either.
          (data.response.employeeID === this.employee.id || (data.response as EmployeeDetailsViewModel).id === this.employee.id)
        ) {
          let forceLogout = false;

          if (data.operationType === OperationTypes.Delete) {
            forceLogout = true;
          } else {
            const employeeData = data.response as EmployeeDetailsViewModel;
            if (!employeeData.isActive) {
              forceLogout = true;
            }
          }

          if (forceLogout) {
            this.authenticationService.logout();
          } else {
            // Someone else (e.g. an Account/User Admin) updated this employee's own
            // department/roles/permissions while their session stays active. Refresh the
            // cached employee - and therefore its permissions - in place, the same way
            // self-service profile/preference changes already do, instead of leaving the
            // browser on stale permissions until a manual reload.
            this.employeeSettingsService.getToken(this.account.id).subscribe();
          }
        }

        if (data.type === ObjectTypes.Allowed_Filter) {
          const filterViewModels = data.response as FilterViewModel[];

          if (
            (this.employee,
            filterViewModels.length > 0 && EmployeeUtil.permissionByFilterViewModel(this.employee, filterViewModels[0]))
          ) {
            switch (data.operationType) {
              case OperationTypes.Create: {
                this.allowedFiltersService.addFilterSettings(filterViewModels);
                break;
              }
              case OperationTypes.Delete: {
                this.allowedFiltersService.deleteFilterSettings(filterViewModels);
                break;
              }
              case OperationTypes.Update: {
                this.allowedFiltersService.updateFilterSettings(filterViewModels);
                break;
              }
              case OperationTypes.Archive: {
                this.allowedFiltersService.deleteFilterSettings(filterViewModels);
                break;
              }
            }
          }
        } else if(data.type == ObjectTypes.Additional_Timezone) {
          this.timezoneService.reInitAdditionalTimeZones();
        } else if (data.type === ObjectTypes.Enum_Based_Localisation) {
          this.localisationService.updateCachedEnumBasedLocalisation(data.response as EnumBasedLocalisationViewModel);
        } else if (this.employeeHasAccess(this.employee.id, data.type, data.response)) {
          switch (data.operationType) {
            case OperationTypes.Create: {
              this.objectEventEmitter.broadcastObjectAdded(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.Delete: {
              this.objectEventEmitter.broadcastObjectDeleted(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.Update: {
              this.objectEventEmitter.broadcastObjectUpdated(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.Archive: {
              this.objectEventEmitter.broadcastObjectArchived(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.RagBreakdownUpdate: {
              this.objectEventEmitter.broadcastRagBreakdownUpdated(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.CreateList: {
              this.objectEventEmitter.broadcastListAdded(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.DeleteList: {
              this.objectEventEmitter.broadcastListDeleted(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.UpdateList: {
              this.objectEventEmitter.broadcastListUpdated(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.FilterLightUpdate: {
              this.objectEventEmitter.broadcastListLightUpdated(data.id, data.type, data.response);
              break;
            }
            case OperationTypes.DocumentDeleted: {
              this.objectEventEmitter.broadcastDocumentDeleted(data.id, data.type, data.response);
              break;
            }
          }
        }
      }
    });
  };

  public addNotificationsListener = () => {
    this.hubConnection.on('notifications', (data: NotificationItemViewModel[]) => {

      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      if (data[0] && data[0].employeeId === this.employee.id) {
        this.notificationsService.addNotifications(data);
      }
    });
  };

  public addHistoryListener = () => {
    this.hubConnection.on('history', (response: [ResponseModel] | ResponseModel) => {
      let data = response[0];

      if(!data)
      {
        data = response;
      }

      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      if (data.accountId == this.employee.accountId) {
        this.objectEventEmitter.broadcastHistoryAdded({objectId: data.id, objectType: data.type, history: data.response as HistoryEntryViewModel});
      }
    });
  };

  public addExportJobListener = () => {
    this.hubConnection.on('exportJob', (data: ExportCompletedBroadcastModel) => {
      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      this.objectEventEmitter.broadcastExportReportUpdated(data);

      // Display notification based on success/error status
      if (data.isError) {
        this.alertService.error(data.title, data.description, this.tastrConfig);
      } else {
        // For successful exports, show success notification with optional view button
        const showBtns = !!data.viewUrl;
        const link = data.viewUrl;

        this.alertService.success(
          data.title,
          data.description,
          this.tastrConfig,
          undefined,
          showBtns,
          link
        );
      }
    });
  };

  public addTimeShiftLockListener = () => {
    this.hubConnection.on('objectsLocked', (data: TimeShiftLockItem[]) => {
      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      this.objectEventEmitter.broadcastTimeShiftLockUpdated(data);
    });
  };

  /**
   * Listened for Async Operation completions (Project Duplication/Gantt Timeline Shifting)
   */
  public addAsyncJobListener = () => {
    this.hubConnection.on('asyncOperation', (data: AsyncOperationCompletedBroadcastModel) => {
      if (!this.account) {
        this.account = this.authenticationService.getCurrentAccount();
      }

      if (!this.employee) {
        this.employee = this.authenticationService.getCurrentEmployee();
      }

      // Display notification based on success/error status
      if (data.isError) {
        this.alertService.error(data.title, data.description, this.tastrConfig);
      } else {
        // For successful exports, show success notification with optional view button
        const showBtns = !!data.viewUrl;
        const link = data.viewUrl;

        this.alertService.success(
          data.title,
          data.description,
          this.tastrConfig,
          undefined,
          showBtns,
          link, true
        );
      }
    });
  };

  private startDataConnection(options: signalR.IHttpConnectionOptions, externalToken?: string) {

     const token = externalToken || this.authService.getAccessToken();

    options.accessTokenFactory = () => token;


    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.configuration.BackEndAPI}/WeTrackHub?uId=${this.employee.id}&aid=${this.account.id}`, options)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Error)
      .configureLogging(signalR.LogLevel.Debug)
      .configureLogging(signalR.LogLevel.Trace)
      .build();

    this.hubConnection
      .start()
      .then(() => {
        this.addListener();
        this.addNotificationsListener();
        this.addHistoryListener();
        this.addExportJobListener();
        this.addTimeShiftLockListener();
        this.addAsyncJobListener();
      })
      .catch((err) => {
        console.log('##ERROR: On start connection');
        console.log(err);

        setTimeout(() => this.startConnection(this.lastEmployeeId, this.lastAccountId, this.lastAccessToken), 5000);
      });

    this.hubConnection.onclose(() => {
      setTimeout(() => this.startConnection(this.lastEmployeeId, this.lastAccountId, this.lastAccessToken), 5000);
    });

    this.hubConnection.onreconnecting((err) => {
      console.log('##ERROR: reconnecting issues');
      console.log(err);
    });
  }

  employeeHasAccess(employeeID: number, type: ObjectTypes, response: any): boolean {
    let hasAccess = false;
    if (type === ObjectTypes.IncidentItem) {
      const incident = response as IncidentItemDetailsViewModel;
      if (incident.incidentItemType === IncidentItemTypes.Log) {
        return true;
      } else if (incident.privacyStatus === PrivacyStatuses.Open) {
        if (EmployeeUtil.IsAdmin(this.employee)) {
          hasAccess = true;
        }

        let hasPerm = EmployeeUtil.hasReadPermission(this.employee, FilterTypes.Incident, [incident.id], false);

        if (!hasPerm) {
          hasPerm = EmployeeUtil.hasOwnerPermissions(this.employee.id, incident.filters);
        }

        if (hasPerm && incident.privacyStatus == PrivacyStatuses.Open) {
          hasAccess = true;
        } else if (incident.departments && incident.departments.length) {
          incident.departments.forEach((dep) => {
            const hasDepartmentPermission = EmployeeUtil.permissionByModuleTypeAndValue(
              this.employee,
              ModuleTypes.Incidents,
              FilterTypes.Department,
              dep.id
            );
            if (hasDepartmentPermission) {
              hasAccess = true;
            }
          });
        }
      } else {
        const isAdmin = EmployeeUtil.IsAdmin(this.employee);
        const perm = EmployeeUtil.permissionByModuleTypeAndValue(
          this.employee,
          ModuleTypes.Incidents,
          FilterTypes.Incident,
          incident.privacyStatus
        );
        if (perm || isAdmin) {
          hasAccess = true;
        }
      }
    } else {
      hasAccess = true;
    }

    return hasAccess;
  }
}
